import type { IncidentReportItem } from '@lmc/ts-common'
import { getJSON } from 'common/services/api'
import { isApiError } from 'common/services/apiError'
import { useQuery } from 'react-query'

const MAX_RETRIES = 3

/** A 4xx, such as the 400 for a log with no report, won't change on a retry. */
const isClientError = (error: unknown) =>
  isApiError(error) && error.status >= 400 && error.status < 500

type UseIncidentReportByLogProps = {
  incidentLogId: string | undefined
  onCreateSuccess?: () => void
  onUpdateSuccess?: () => void
  onDeleteSuccess?: () => void
  /**
   * @default true
   */
  enabled?: boolean
  /**
   * Fetch from the server on mount even when the cached copy is still fresh.
   * @default false
   */
  alwaysRefetchOnMount?: boolean
}

export const GET_INCIDENT_REPORT_BY_LOG_URL = `/api/list/incident-report/by-log`

export const incidentReportByLogQueryKey = (
  incidentLogId: string | undefined
) => ['incident-report', incidentLogId]

const useIncidentReportByLog = <
  T extends IncidentReportItem = IncidentReportItem
>({
  incidentLogId,
  enabled = true,
  alwaysRefetchOnMount = false
}: UseIncidentReportByLogProps) => {
  const incidentLogIdExists = incidentLogId !== undefined
  const isEnabled = incidentLogIdExists && enabled === true

  const {
    data,
    isLoading: isIncidentReportLoading,
    isFetchedAfterMount,
    isError,
    refetch
  } = useQuery<{
    data: T
  }>(
    incidentReportByLogQueryKey(incidentLogId),
    () =>
      getJSON({ url: `${GET_INCIDENT_REPORT_BY_LOG_URL}/${incidentLogId}` }),
    {
      enabled: isEnabled,
      retry: (failureCount, error) =>
        !isClientError(error) && failureCount < MAX_RETRIES,
      // With no data, a refetch puts the query back into loading, which
      // screens show as a full-page spinner.
      refetchOnWindowFocus: (query) => !isClientError(query.state.error),
      ...(alwaysRefetchOnMount ? { refetchOnMount: 'always' as const } : {})
    }
  )

  return {
    incidentReport: data?.data,
    isIncidentReportLoading,
    isFetchedAfterMount,
    isError,
    refetch
  }
}

export default useIncidentReportByLog
